import React, { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { FormData, GroupType, LogoData } from '../api/api';
import { allowedDraftField, allowedPublishField } from '../constants/formConstants';
import { useAxios } from '../context/AxiosContext';
import { combineFieldArrays, omitExtraFields, transformGroups } from '../utils/outcomingDataTransformationHelper';
import { useModal } from '../components/Modal';
import { addTrackEvent } from '../utils/piwikUtils';
import { PiwikName } from '../@types/common';

const toBase64 = (file): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    // @ts-ignore
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export const setLogo = async (logo: string | any[]): Promise<LogoData> => {
  const returnLogo: LogoData = {
    action: 'KEEP',
    image: ''
  };
  if (!logo || !Array.isArray(logo)) {
    returnLogo.action = 'DELETE';
  } else if (logo[0].preview && logo[0].size) {
    returnLogo.action = 'UPDATE';
    returnLogo.image = await toBase64(logo[0]);
  } else {
    returnLogo.action = 'KEEP';
  }

  return returnLogo;
};

export function useFormData({ type, id }: { type?: string; id?: string }) {
  const { showModal } = useModal();
  const queryCache = useQueryClient();
  const { authAxios } = useAxios();
  const entity: string = type === 'draft' ? 'draft' : 'group';

  const fetchLogo = useCallback(
    async (id?: string) => {
      const response = await authAxios.get<FormData>(`/${entity}/${id}/logo`);
      if (response?.data?.file) {
        const string = 'data:image/jpeg;base64,' + response.data.file;
        return [{ preview: string }];
      }
    },
    [authAxios, entity]
  );

  const fetchFormData = useCallback(
    async (_: string, id?: string) => {
      const { data } = await authAxios.get<FormData>(`/${entity}/${id}`);
      const logo = await fetchLogo(id);
      return { ...data, logo };
    },
    [authAxios, entity, fetchLogo]
  );

  const deleteFormData = useCallback(
    async ({ type, id }: { type: string; id: string | null }) => {
      const response = await authAxios.delete<FormData>(`/${type}/${id}`);
      return response.data;
    },
    [authAxios]
  );

  const updateFormData = useCallback(
    async (data) => {
      const { _id, ...updateData } = data;
      // transform outgoing data
      const combinedArrays = combineFieldArrays(updateData);
      transformGroups(combinedArrays, false);
      const preparedData = omitExtraFields(combinedArrays, allowedDraftField);

      preparedData.logo = await setLogo(data.logo);
      const response = await authAxios.put<FormData>(`/${type}/${id}`, preparedData);
      return response.data;
    },
    [authAxios, id, type]
  );

  const insertFormData = useCallback(
    async ({ data, type }: { data: any; type: string }) => {
      // transform outgoing data
      const combinedArrays = combineFieldArrays(data);
      transformGroups(combinedArrays);
      combinedArrays.logo = await setLogo(data.logo);

      const response = await authAxios.post<FormData>(`/${type}`, { ...combinedArrays });
      return response.data;
    },
    [authAxios]
  );

  const { status, data, isFetching } = useQuery(['form', type, id], () => fetchFormData('form', id), {
    refetchOnWindowFocus: false,
    enabled: Boolean(id && type)
  });

  const submitData = useCallback(
    async ({ data, type }) => {
      const { _id, ...updateData } = data;

      const combinedArrays = combineFieldArrays(updateData);
      transformGroups(combinedArrays);
      const preparedData = omitExtraFields(combinedArrays, allowedPublishField);

      const isCreate = type === GroupType.DRAFT;
      const logo = await setLogo(data.logo);
      if ((type === GroupType.DRAFT && logo.action !== 'KEEP') || type !== GroupType.DRAFT) {
        preparedData.logo = logo;
      }
      const response = await authAxios.post<FormData>('/publish', { ...preparedData, id: _id, isCreate });
      return response.data;
    },
    [authAxios]
  );

  const insertMutation = useMutation(insertFormData, {
    onSuccess: (data) => {
      queryCache.invalidateQueries('form');
      queryCache.invalidateQueries('drafts');
      addTrackEvent(PiwikName.NEW_GROUP);
      toast.success(`Successfully created ${data.name} #${data.planNumber}`, { position: 'top-center' });
    },
    onError: (error: any) => {
      toast.error(`Failed to create group (${error.message})`, { position: 'top-center' });
    }
  });

  const updateMutation = useMutation(updateFormData, {
    onSuccess: () => {
      const groupType = type === GroupType.DRAFT ? 'drafts' : 'groups';
      queryCache.invalidateQueries('form');
      queryCache.invalidateQueries(groupType);
      toast.success('Data was updated', { position: 'top-center' });
    },
    onError: (error: any) => {
      toast.error(error.message, { position: 'top-center' });
    }
  });

  const deleteMutation = useMutation(deleteFormData, {
    onSuccess: () => {
      queryCache.invalidateQueries('drafts');
      addTrackEvent(PiwikName.DELETE_DRAFT);
      toast.success('Data was deleted');
    },
    onError: (error: any) => {
      toast.error(error.message, { position: 'top-center' });
    }
  });

  const publishMutation = useMutation(submitData, {
    onSuccess: () => {
      const groupType = type === GroupType.DRAFT ? 'drafts' : 'groups';
      queryCache.invalidateQueries('form');
      queryCache.invalidateQueries(groupType);
      showModal({
        size: 'prompt',
        header: 'Your groups have been submitted for processing.',
        content: (
          <section className="p-6 font-bold">
            <p>Please allow up to 1 business day for activation.</p>
          </section>
        ),
        actions: [{ title: 'OK', kind: 'primary' }]
      });
      addTrackEvent(PiwikName.PUBLISH_SUCCESS);
    },
    onError: () => {
      addTrackEvent(PiwikName.PUBLISH_FAIL);
      toast.error('Unable to publish changes, please try again', { position: 'top-center' });
    }
  });

  return {
    formData: data,
    isFormFetching: isFetching,
    setLogo,
    removeFormData: deleteMutation.mutate,
    updateFormData: updateMutation.mutate,
    insertFormData: insertMutation.mutate,
    submitData: publishMutation.mutate,
    formStatus: status
  };
}
